/**
 * Draws text using breaking-word strategy
 * when text width overflow fitWidth.
 * @returns Next Y coord available
 */
export function printAtWithBreakStrategy(
  onRender: (text: string, x: number, y: number, fontSize?: number) => void,
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  lineHeight: number,
  fitWidth: number = 0,
  numberOfLines: number = 99
) {
  if (fitWidth <= 0) {
    onRender(text, x, y);
    return lineHeight;
  }

  let overflow = false;
  let words = text.split(' ');
  let currentLine = 0;
  let idx = 1;

  while (words.length > 0 && idx <= words.length) {
    const str = words.slice(0, idx).join(' ');
    const { width: textWidth } = context.measureText(str);
    if (textWidth > fitWidth) {
      if (idx == 1) {
        idx = 2;
      }

      if (currentLine + 1 < numberOfLines) {
        const partialStr = words.slice(0, idx - 1).join(' ');
        const strHasNoSpaces = partialStr === str;

        if (strHasNoSpaces) {
          let indexToForceBreak = text.length - 1;

          while (
            context.measureText(str.slice(0, indexToForceBreak)).width >
            fitWidth
          ) {
            indexToForceBreak--;
          }

          words = [...splitAt(indexToForceBreak)(str), ...words.slice(idx - 1)];

          onRender(words[idx - 2], x, y + lineHeight * currentLine);
        } else {
          onRender(partialStr.trim(), x, y + lineHeight * currentLine);
        }
      } else {
        const match = context.font.match(/\d+px/);
        const strRest = words.join(' ');
        if (match) {
          const originalFontSize = Number(match[0].replace('px', ''));
          let fontSize = originalFontSize;

          while (
            context.measureText(strRest).width >= fitWidth &&
            fontSize > 1
          ) {
            fontSize--;
            context.font = context.font.replace(/\d+px/, `${fontSize}px`);
            context.textBaseline = 'bottom';
          }

          const offsetTop = (4 * originalFontSize) / (fontSize + lineHeight);

          onRender(
            strRest,
            x,
            y + lineHeight * currentLine + offsetTop,
            fontSize
          );

          context.textBaseline = 'alphabetic';
          overflow = true;
        }
      }

      currentLine++;
      words = words.splice(idx - 1);
      idx = 1;
    } else {
      idx++;
    }
  }

  if (idx > 0 && currentLine < numberOfLines) {
    onRender(words.join(' '), x, y + lineHeight * currentLine);
  }

  return y + lineHeight * (currentLine - (overflow ? 1 : 0));
}

/**
 * Calculates the dimensions and offsets
 * to make an image fit in a desired
 * container using `cover` strategy.
 * @returns parameters to use on DrawImage
 */
export const cover = fit(false);

/**
 * Calculates the dimensions and offsets
 * to make an image fit in a desired
 * container using `contain` strategy.
 * @returns parameters to use on DrawImage
 */
export const contain = fit(true);

function fit(contains: boolean) {
  return (
    parentWidth: number,
    parentHeight: number,
    childWidth: number,
    childHeight: number,
    scale = 1,
    offsetX = 0.5,
    offsetY = 0
  ) => {
    const childRatio = childWidth / childHeight;
    const parentRatio = parentWidth / parentHeight;
    let width = parentWidth * scale;
    let height = parentHeight * scale;

    if (contains ? childRatio > parentRatio : childRatio < parentRatio) {
      height = width / childRatio;
    } else {
      width = height * childRatio;
    }

    return {
      width,
      height,
      offsetX: (parentWidth - width) * offsetX,
      offsetY: (parentHeight - height) * offsetY
    };
  };
}

/**
 * Draws a rounded image
 * @retuns void
 */
export function drawRoundedImage(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  border?: { width: number; color: string }
) {
  ctx.save();

  if (border) {
    ctx.strokeStyle = border.color;
    ctx.lineWidth = border.width;
  }

  ctx.beginPath();
  ctx.arc(x + width / 2, y + height / 2, radius, 0, Math.PI * 2, true);
  ctx.stroke();
  ctx.closePath();
  ctx.clip();

  ctx.drawImage(img, x, y, width, height);

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2, true);
  ctx.clip();
  ctx.closePath();

  ctx.restore();
}

type TRadius = {
  [i: string]: number;
  tl: number;
  tr: number;
  br: number;
  bl: number;
};
/**
 * Draws a rounded rectangle using the current state of the canvas.
 * If you omit the last three params, it will draw a rectangle
 * outline with a 5 pixel border radius
 * @param {CanvasRenderingContext2D} ctx
 * @param {Number} x The top left x coordinate
 * @param {Number} y The top left y coordinate
 * @param {Number} width The width of the rectangle
 * @param {Number} height The height of the rectangle
 * @param {Number} [radius = 5] The corner radius; It can also be an object
 *                 to specify different radii for corners
 * @param {Number} [radius.tl = 0] Top left
 * @param {Number} [radius.tr = 0] Top right
 * @param {Number} [radius.br = 0] Bottom right
 * @param {Number} [radius.bl = 0] Bottom left
 * @param {Boolean} [fill = false] Whether to fill the rectangle.
 * @param {Boolean} [stroke = true] Whether to stroke the rectangle.
 * @returns void
 */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number | TRadius,
  fill: boolean = false,
  stroke: boolean = true
) {
  if (typeof stroke === 'undefined') {
    stroke = true;
  }
  if (typeof radius === 'undefined') {
    radius = 5;
  }
  if (typeof radius === 'number') {
    radius = { tl: radius, tr: radius, br: radius, bl: radius };
  } else {
    const defaultRadius: TRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
    for (const side in defaultRadius) {
      radius[side] = radius[side] || defaultRadius[side];
    }
  }
  ctx.beginPath();
  ctx.moveTo(x + radius.tl, y);
  ctx.lineTo(x + width - radius.tr, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
  ctx.lineTo(x + width, y + height - radius.br);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius.br,
    y + height
  );
  ctx.lineTo(x + radius.bl, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
  ctx.lineTo(x, y + radius.tl);
  ctx.quadraticCurveTo(x, y, x + radius.tl, y);
  ctx.closePath();
  if (fill) {
    ctx.fill();
  }
  if (stroke) {
    ctx.stroke();
  }
}

/**
 * Creates an HTMLImage from an url.
 * @returns HTMLImageElement
 */
export function getHTMLImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onerror = reject;
    image.onload = () => resolve(image);
    image.src = url;
  });
}

const splitAt = (index: number) => (x: string) => [
  x.slice(0, index),
  x.slice(index)
];
